import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  CreateRecipeRequest,
  Recipe,
  UpdateRecipeRequest,
} from '../../shared/models/recipe.model';
import {
  PaginatedResult,
  PaginationFilter,
} from '../../shared/models/pagination.model';
import { CrudService } from '../../shared/models/crud-service.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class RecipeService implements CrudService<Recipe> {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.backendUrl + 'recipes';

  loadWithFilter(
    filter: PaginationFilter
  ): Observable<PaginatedResult<Recipe>> {
    let httpParams = new HttpParams()
      .set('page', filter.page.toString())
      .set('limit', filter.limit.toString());

    if (filter.search) {
      httpParams = httpParams.set('search', filter.search);
    }

    return this.http.get<PaginatedResult<Recipe>>(this.apiUrl, {
      params: httpParams,
    });
  }

  loadById(id: string): Observable<Recipe> {
    return this.http.get<Recipe>(`${this.apiUrl}/${id}`);
  }

  create(recipeData: CreateRecipeRequest): Observable<Recipe> {
    return this.http.post<Recipe>(this.apiUrl, recipeData);
  }

  update(id: string, recipeData: UpdateRecipeRequest): Observable<Recipe> {
    return this.http.patch<Recipe>(`${this.apiUrl}/${id}`, recipeData);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  deleteMany(ids: string[]): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}`, {
      body: {
        ids,
      },
    });
  }

  loadAllergensStatistics(): Observable<{ allergen: string; count: number }[]> {
    return this.http.get<{ allergen: string; count: number }[]>(
      `${this.apiUrl}/dashboard/allergens`
    );
  }

  loadCategoriesStatistics(): Observable<
    { category: string; count: number }[]
  > {
    return this.http.get<{ category: string; count: number }[]>(
      `${this.apiUrl}/dashboard/categories`
    );
  }

  generateImage(recipeId: string): Observable<string> {
    return this.http.post(
      `${this.apiUrl}/${recipeId}/image`,
      {},
      { responseType: 'text' }
    );
  }

  getTotalCount(): Observable<{ total: number }> {
    return this.http.get<{ total: number }>(`${this.apiUrl}/count`);
  }

  duplicate(id: string): Observable<Recipe> {
    return this.http.post<Recipe>(`${this.apiUrl}/${id}/duplicate`, {});
  }
}
